import { v4 } from "uuid";

import { AiChatState } from "@intelligems/ig-types";
import { experienceCreationChatSessionHistoryQueryOptions } from "@src/api/ai/experience-creation-chat/use-get-experience-creation-chat-session-history";
import { queryClient } from "@src/queryClient";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import {
  clearAiChat,
  handleAiChatAction,
  restoreChatState,
} from "@src/store/slices/aiChat";
import { experienceBuilderAiSlice } from "@src/store/slices/experienceBuilder/ai/slice";
import { buildCreationBrief } from "@src/store/slices/experienceBuilder/ai/utils/buildCreationBrief";
import { setAiChat } from "@src/store/slices/global";

/**
 * Restores the merchant's original experience-creation chat and appends a
 * summary of what just happened in Viv. Called from `closeBuilder` only when
 * the builder was entered via that chat's own hand-off
 * (`originExperienceCreationSessionId` set) — the merchant's mental model is
 * "I was talking to Ellie, jumped into the builder, now I'm back," so this
 * re-fetches and restores THAT conversation rather than starting a new one.
 *
 * Deliberately NOT the same mechanism as `handoffBuilderToCreation` (the
 * mid-conversation "hand off now" tool action): this doesn't open the
 * creation chat panel or fire a live model turn — the merchant stays on the
 * test-setup page, and the restored transcript + summary just sit there,
 * client-rendered, whenever they next open the creation chat.
 *
 * Takes the Viv transcript as a parameter rather than reading it from state:
 * the caller must snapshot it BEFORE `closeBuilder`'s own `clearAiChat` (and
 * the builder-close listener's) can wipe it.
 */
export const seedCreationHandoffOnExit = createAsyncThunk<void, AiChatState>(
  "experienceBuilderAi/seedCreationHandoffOnExit",
  async (vivChat, { dispatch, getState }) => {
    const summary = buildCreationBrief(vivChat);
    if (!summary) {
      return;
    }

    const originSessionId =
      experienceBuilderAiSlice.selectors.getOriginExperienceCreationSessionId(
        getState()
      );

    dispatch(clearAiChat());

    const original = originSessionId
      ? await queryClient.fetchQuery(
          experienceCreationChatSessionHistoryQueryOptions(originSessionId)
        )
      : null;
    if (original) {
      dispatch(restoreChatState(original));
    } else {
      // Fallback: the original session couldn't be re-fetched (deleted, not
      // yet persisted, request failed) — still hand back the Viv summary in
      // a fresh session rather than dropping it entirely.
      dispatch(handleAiChatAction([{ type: "setSessionId", sessionId: v4() }]));
    }

    const timestamp = Date.now();
    dispatch(
      handleAiChatAction({
        type: "addMessage",
        turnId: v4(),
        nodeId: v4(),
        nodeName: "Atlas",
        message: {
          id: v4(),
          role: "assistant",
          timestamp,
          parts: [
            {
              id: v4(),
              type: "text",
              text: `Here's a summary of what happened while you were in the Visual Builder:\n\n${summary}`,
              timestamp,
            },
          ],
        },
      })
    );
    dispatch(
      setAiChat([{ key: "mode", value: "experience-creation-single-agent" }])
    );
  }
);
